import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InfiniteHitsProvided } from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  InstantSearch,
} from 'react-instantsearch-dom'
import { Fade, Icons, InfoModal, InformationModal } from 'utoniq-ui'

import {
  AddItemToCartMutationVariables,
  CryptoWallet,
  EscottMasterInput,
  ItemResaleRaffleUser,
  ItemType,
  Maybe,
  PaymentMethod,
  PrepaidPointsPaymentSourceFragment,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
  SkuTagFragment,
  StoreItemDetailFragment,
  StoreItemDetailSkuFragment,
  StoreItemFragment,
  User,
  UserDeliveryInfoFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  ItemDetailBox,
  ItemDetailBoxSkeleton,
  PointChargeDrawer,
  PointDisplayPurchase,
  SkuUnitSelectBottomSheet,
  StoreButtonBox,
  StoreButtonBoxSkeleton,
  StoreButtonResaleBox,
  StoreItemArea,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import {
  CreateCryptoWalletConfirmModal,
  CryptoWalletConnectDrawer,
} from '@/components/organisms/nft'
import { SkuType } from '@/components/organisms/store/SkuType'
import { useStoreButton } from '@/components/organisms/store/StoreButtonBox/useStoreButton'
import {
  BuyNowModal,
  BuyNowModalProps,
} from '@/components/pages/ArtistStoreItemDetail/template/BuyNowModal'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { StoreStateResults } from '@/components/pages/StoreRequestProvidableItemSearch/template'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { User as AuthUser } from '@/core/auth'
import { DateTime } from '@/core/entities'
import {
  individualDisplayResponsive,
  individualHideWallet,
} from '@/core/individual'
import { App } from '@/core/native'
import { encodeSkuId } from '@/core/sku'
import * as algolia from '@/lib/algolia'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  currentUser: AuthUser | null
  user: Maybe<
    Pick<
      User,
      | 'id'
      | 'isPhoneNumberVerified'
      | 'formattedPhoneNumber'
      | 'fullName'
      | 'phoneNumber'
    > & {
      cryptoWallet?: Maybe<Pick<CryptoWallet, 'address'>> | undefined
    } & UserDeliveryInfoFragment
  >
  userActiveResaleItemRaffle: Maybe<Pick<ItemResaleRaffleUser, 'id' | 'itemId'>>
  artistId: string
  itemId: string
  loading: boolean
  hasItem: boolean
  itemPurchasing: boolean
  prepaidPointId: string
  isLoggedIn: boolean
  userLoggedInId: string
  addingToCart: boolean
  loadingItemWithSku: boolean
  item: Maybe<StoreItemDetailFragment | StoreItemDetailSkuFragment>
  purchasablePlanNames: string[]
  point: number
  formattedPoint: string
  isConnect: boolean
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  pointChargeLoading: boolean
  isCardRegistered: boolean
  verifyingSMSCode: boolean
  sendingVerificationLoading: boolean
  openSMSVerificationModal: BuyNowModalProps['openSMSVerificationModal']
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  userCartCount: number
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
  skuButtonActive?: boolean
  skuWillResaleAvailable?: boolean
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
}

export type Action = {
  connectMetamask: () => Promise<void>
  connectToWeb3auth: () => Promise<void>
  onPurchaseCharge: (
    plan: PurchasablePrepaidPointDetailFragment,
    paymentMethod: Maybe<PaymentMethod>,
    escottInput: EscottMasterInput
  ) => Promise<void>
  onPurchaseItem: (
    count: number,
    price: number,
    skuId?: string | undefined
  ) => Promise<void>
  onUserInfoSetting: BuyNowModalProps['onUserInfoSetting']
  onSendVerificationSMS: BuyNowModalProps['onSendVerificationSMS']
  onCloseSMSVerificationModal: BuyNowModalProps['onCloseSMSVerificationModal']
  onCreatePayment: BuyNowModalProps['onCreatePayment']
  onDeliveryInfoSetting: BuyNowModalProps['onDeliveryInfoSetting']
  onResendVerificationSMS: BuyNowModalProps['onResendVerificationSMS']
  onVerificationPhoneNumber: BuyNowModalProps['onVerificationPhoneNumber']
  onAddToCart: (
    inputs: AddItemToCartMutationVariables['inputs']
  ) => Promise<number | void>
  onClickShare: () => Promise<void>
  onGetArtistStoreItemDetailWithSku: (skuId?: string) => void
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

const client = algolia.client()

export const ArtistStoreItemDetailTemplate: Template<State, Action> = ({
  state,
  onAddToCart,
  connectMetamask,
  connectToWeb3auth,
  onPurchaseCharge,
  onPurchaseItem,
  onUserInfoSetting,
  onSendVerificationSMS,
  onCloseSMSVerificationModal,
  onCreatePayment,
  onDeliveryInfoSetting,
  onResendVerificationSMS,
  onVerificationPhoneNumber,
  onClickShare,
  onGetArtistStoreItemDetailWithSku,
  onCloseRequire3DSecureModal,
  onCloseExpiredCardModal,
  onClosePaymentErrorModal,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { routerNames, generatePath, routerPages, router } = useRouter<{
    artistId: string
    itemId: string
  }>()
  const { analytics } = useAnalytics()
  const { showMessage, showError } = useMessage()
  const { handleLoginModal } = usePrivateAction()
  const publishedAtTimestampRef = useRef<number>(DateTime.nowUnixMilliseconds())
  const itemResultRef = useRef<{ items: StoreItemFragment[]; page: number }>({
    items: [],
    page: 1,
  })
  const instantSearchRef = useRef<InstantSearch>(null)
  const [openConfirmFreeItem, setOpenConfirmFreeItem] = useState<boolean>(false)
  const [searchState, setSearchState] = useState({ page: 1 })
  const [openNoEnoughPoint, setOpenNoEnoughPoint] = useState<boolean>(false)
  const [skuId, setSkuId] = useState<string | undefined>()
  const [skuTag, setSkuTag] = useState<SkuTagFragment | undefined>(undefined)
  const [openNftConnectDrawer, setOpenNftConnectDrawer] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [openWalletCreateConfirmModal, setOpenWalletCreateConfirmModal] =
    useState(false)
  const [openNftInfoModal, setOpenNftInfoModal] = useState<boolean>(false)
  const [purchaseNumber, setPurchaseNumber] = useState<number>(1)
  const [openPersonalInfoModal, setOpenPersonalInfoModal] = useState(false)
  /** ポイントチャージ用ドロワーの表示状態 */
  const [openPointPurchaseDrawer, setOpenPointPurchaseDrawer] =
    useState<boolean>(false)
  /** SKUを選択していない状態で購入しようとした時に表示されるUI */
  const [skuUnitSelectBottomSheetType, setSkuUnitSelectBottomSheetType] =
    useState<'buyNow' | 'cart' | 'close'>('close')
  const [openBuyNow, setOpenBuyNow] = useState<boolean>(false)

  const isNftItem = !!state.item?.nft?.chains

  const totalPurchasableCount = useMemo<number>(() => {
    if (!state.item?.purchasableUsers?.length) {
      return 0
    }
    return state.item?.purchasableUsers
      .filter(
        purchasableUser => purchasableUser.userId === state.userLoggedInId
      )
      .reduce(
        (total, purchasableUser) =>
          total + (purchasableUser.purchasableCount ?? 0),
        0
      )
  }, [state.item?.purchasableUsers, state.userLoggedInId])

  const purchaseLimitByUser = useMemo<number>(() => {
    if (typeof state.item?.purchaseLimitByUser !== 'number') {
      return totalPurchasableCount ?? 10
    }
    if (state.item.purchaseLimitByUser < totalPurchasableCount) {
      return state.item.purchaseLimitByUser
    }
    return state.item.purchaseLimitByUser - totalPurchasableCount
  }, [state.item?.purchaseLimitByUser, totalPurchasableCount])

  const {
    label: buttonLabel,
    disabled: buttonDisabled,
    restrected,
    maximumPurchasableNumber,
  } = useStoreButton({
    item: state.item,
    isNft: isNftItem,
    userId: state.user?.id,
    isLoggedIn: state.isLoggedIn,
    isWalletConnect: state.isConnect,
    purchaseLimitByUser,
  })
  const { formattedItemPrice, itemPrice } = useMemo(() => {
    if (!isNaN(Number(skuTag?.price ?? undefined))) {
      return {
        itemPrice: skuTag?.price,
        formattedItemPrice: skuTag?.formattedPrice ?? '0',
      }
    }
    if (state.item?.price) {
      return {
        itemPrice: state.item?.price,
        formattedItemPrice: state.item?.formattedPrice ?? '0',
      }
    }
    return {
      itemPrice: 0,
      formattedItemPrice: '0',
    }
  }, [
    skuTag?.formattedPrice,
    skuTag?.price,
    state.item?.formattedPrice,
    state.item?.price,
  ])

  const handleChangePurchaseNumber = (number: number) =>
    setPurchaseNumber(number)

  const onConnectWeb3auth = () => {
    setConnecting(true)
    return connectToWeb3auth()
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenWalletCreateConfirmModal(false)
        setOpenNftConnectDrawer(false)
      })
  }

  const onConnectMetamask = () => {
    setConnecting(true)
    return connectMetamask()
      .then(() => {
        showMessage(t('connected'))
      })
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenNftConnectDrawer(false)
      })
  }

  useEffect(() => {
    setSearchState(prevState => ({ ...prevState, page: 1 }))
  }, [state.item])

  /**
   * hack to reset sku value when url path changed
   */
  useEffect(() => {
    setSkuTag(undefined)
    setSkuId(undefined)
    setPurchaseNumber(1)
    setOpenPointPurchaseDrawer(false)
  }, [router.pathname])

  useEffect(() => {
    const baseLayoutElement = document.getElementsByClassName('BaseLayout')
    baseLayoutElement?.[0]?.scrollTo({ top: 0 })
  }, [router.pathname])

  useEffect(() => {
    if (state.item) {
      analytics?.track('view_item', {
        currency: 'JPY',
        value: state.item.price,
        items: itemResultRef.current.items.map(item => ({
          item_id: item.id,
          item_name: item.title,
          item_category: ConversionCategories.storeItem,
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: itemResultRef.current.page,
          item_category5: state.artistId,
          price: item.price,
        })),
      })
    }
  }, [analytics, state.artistId, state.item])

  const handleOnCompleted = useCallback(
    (items: StoreItemFragment[], { page }: { page: number }) => {
      itemResultRef.current = { items, page }
      analytics?.track('view_item_list', {
        item_list_id: `artist_store_list_related_${state.artistId}`,
        item_list_name: 'Artist Store Related Items',
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.title,
          item_category: ConversionCategories.storeItem,
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: page,
          item_category5: state.artistId,
          price: item.price,
        })),
      })
    },
    [analytics, state.artistId]
  )

  const handleOnSelectItem = useCallback(() => {
    analytics?.track('select_item', {
      item_list_id: `artist_store_list_${state.artistId}`,
      item_list_name: 'Artist Store Items',
      items: itemResultRef.current.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        item_category: ConversionCategories.storeItem,
        item_category2: item.type,
        item_category3: App.getPlatform(),
        item_category4: itemResultRef.current.page,
        item_category5: state.artistId,
        price: item.price,
      })),
    })
  }, [analytics, state.artistId])

  const onAnalyzeCheckout = () => {
    analytics?.track('add_to_cart', {
      item_list_id: `artist_store_list_${state.artistId}`,
      item_list_name: 'Artist Store Items',
      isInstant: true,
      items: [
        {
          item_id: state.item?.id,
          item_name: state.item?.title,
          item_category: ConversionCategories.storeItem,
          item_category2: state.item?.type,
          item_category3: App.getPlatform(),
          item_category4: null,
          item_category5: state.artistId,
          price: state.item?.price,
          quantity: purchaseNumber,
        },
      ],
    })

    analytics?.track('begin_checkout', {
      item_list_id: `artist_store_list_${state.artistId}`,
      item_list_name: 'Artist Store Items',
      isInstant: true,
      items: [
        {
          item_id: state.item?.id,
          item_name: state.item?.title,
          item_category: ConversionCategories.storeItem,
          item_category2: state.item?.type,
          item_category3: App.getPlatform(),
          item_category4: null,
          item_category5: state.artistId,
          price: state.item?.price,
          quantity: purchaseNumber,
        },
      ],
    })
  }

  /** カート追加処理。未ログインの場合、ゲストログインを許可する */
  const handleAddToCart = () => {
    if (state.item?.skuSettings?.units.length && !skuTag?.id) {
      return setSkuUnitSelectBottomSheetType('cart')
    }
    onAddToCart({
      itemId: state.item?.id || router.query.itemId,
      skuId: skuTag?.id,
      count: purchaseNumber,
    })
  }

  const handleBuyNow = handleLoginModal(() => {
    if (state.item?.skuSettings?.units.length && !skuTag?.id) {
      return setSkuUnitSelectBottomSheetType('buyNow')
    }
    if (!itemPrice) {
      setOpenConfirmFreeItem(true)
      return
    }
    setOpenBuyNow(true)
  })

  // 代金や手数料など、支払のすべてをブレイクダウンした配列
  const statements = [
    {
      label:
        state.item?.paymentType !== UserPaymentType.Cash
          ? t('totalItemPoints')
          : t('totalItemAmount'),
      value: (itemPrice ?? 0) * purchaseNumber,
    },
    ...(state.item?.fees?.collections || []).map(fee => {
      const feeValue = fee?.fee ?? 0
      return {
        label: fee?.feeName,
        value: fee?.per === 'order' ? feeValue : feeValue * purchaseNumber,
      }
    }),
    ...(state.item?.type === ItemType.Merch
      ? [
          {
            label: state.item?.fees?.shipping?.feeName,
            value: state.item?.fees?.shipping?.fee,
          },
        ]
      : []),
  ]
  //skuがあればsku単位のwillResaleAvailable,なければitem単位のwillResaleAvailableを使う
  const willResaleAvailable = state.item?.skuSettings?.units
    ? !!state.skuWillResaleAvailable
    : !!state.item?.willResaleAvailable

  const skuButtonActive = !state.skuButtonActive
    ? !!state.item?.willResaleAvailable
    : state.skuButtonActive

  const totalPrice = statements.reduce(
    (acc, curr) => acc + (curr?.value ?? 0),
    0
  )

  const displayResponsive = individualDisplayResponsive()

  if (!config.modules.item) {
    return <NotFound useNextRouter={false} />
  }

  if (!state.loading && !state.item) {
    return <NotFound useNextRouter={false} />
  }

  /**
   * NFT 停止に向けて、NFTアイテムの詳細ページを表示しない
   * NFT 機能停止後には削除して問題ない
   */
  if (isNftItem) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ArtistStoreItemDetail} />

      <BaseLayout
        header={{
          back: true,
          defaultBackPage:
            state.artistId &&
            generatePath(routerPages.ArtistTop, {
              artistId: state.artistId,
            }),
          right: state.isLoggedIn &&
            !!config.modules.prepaidPoint &&
            !individualHideWallet() && (
              <PointDisplayPurchase
                point={state.formattedPoint}
                onClick={() => setOpenPointPurchaseDrawer(true)}
              />
            ),
          ...(displayResponsive ? { responsive: true, maxWidth: 1120 } : {}),
        }}
        customNavigation={
          state.item &&
          !restrected && (
            <StoreButtonBox
              label={buttonLabel}
              disabled={buttonDisabled}
              purchaseLimitPerOrder={state.item.purchaseLimitPerOrder}
              purchaseLimitByUser={purchaseLimitByUser}
              item={state.item}
              formattedItemPrice={formattedItemPrice}
              maximumPurchasableNumber={maximumPurchasableNumber}
              paymentType={state.item.paymentType}
              loading={state.addingToCart || state.loadingItemWithSku}
              purchaseNumber={purchaseNumber}
              onChangePurchaseNumber={handleChangePurchaseNumber}
              onAddToCart={handleAddToCart}
              onBuyNow={handleBuyNow}
              skuId={skuId}
              willResaleAvailable={willResaleAvailable}
              responsive={displayResponsive}
              maxWidth={displayResponsive ? 1120 : undefined}
            />
          )
        }
        {...(displayResponsive
          ? {
              customNavigationSx: {
                maxWidth: '100%',
              },
              maxWidth: 1120,
            }
          : {})}
      >
        {state.loading || !state?.item ? (
          <Stack sx={{ px: 5 }}>
            <ItemDetailBoxSkeleton hiddenSupplementLink />
            <StoreButtonBoxSkeleton />
          </Stack>
        ) : (
          <Stack sx={{ px: 5 }}>
            {config.modules.nft && isNftItem && !state.isConnect && (
              <Stack spacing={4} sx={{ px: 6, py: 4 }}>
                <Stack display="flex" flexDirection="row">
                  <Icons.Error
                    sx={{ color: 'red', height: 20, width: 20, mr: 2 }}
                  />
                  <Typography variant="caption">
                    {t('nftConnectTitle')}
                  </Typography>
                </Stack>
                <Stack display="flex" justifyContent="center">
                  <Button
                    onClick={() => setOpenNftConnectDrawer(true)}
                    sx={{ width: 244, mx: 'auto' }}
                  >
                    {t('nftConnect')}
                  </Button>
                </Stack>
              </Stack>
            )}
            <Fade>
              <Box sx={{ pb: 4 }}>
                <ItemDetailBox
                  userItem={
                    {
                      item: state.item,
                      number: 0,
                      isSystemProtected: false,
                      isUserProtected: false,
                      serials: [],
                    } as any
                  }
                  showPreSale={!!state.item.preSale}
                  isPremium={state.item.isPremium}
                  purchasablePlanNames={state.purchasablePlanNames}
                  skuUnit={skuTag}
                  userId={state.user?.id}
                  hiddenPurchaseLimit={false}
                  viewable
                  hiddenInfo={!!state.item.hideSalesNumber}
                  hidden={{ trade: true, expansion: true }}
                  onClickNftIcon={() => setOpenNftInfoModal(true)}
                  isPickUp={!!state.item?.collection?.isEventPickup}
                  isMerch={state.item.type === ItemType.Merch}
                  onClickShare={onClickShare}
                  purchaseLimitByUser={purchaseLimitByUser}
                />
              </Box>
            </Fade>

            {!state.item.preSale && (
              <SkuType
                value={skuId}
                disabled={skuButtonActive === true ? false : buttonDisabled}
                paymentType={state.item?.paymentType}
                skuSettings={state.item?.skuSettings}
                issuedNumber={state.item?.issuedNumber}
                isResaleEnable={
                  skuButtonActive
                    ? skuButtonActive
                    : state.item?.isResaleEnable ??
                      state.item?.isResaleAvailable
                }
                onSelect={sku => {
                  setSkuId(sku?.id)
                  setSkuTag(sku)
                  onGetArtistStoreItemDetailWithSku(sku?.id)
                }}
                hasAlertText={false}
              />
            )}
            {state.item.metadata?.ticket?.note && (
              <Stack spacing={1} sx={{ my: 4 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icons.Info fontSize="small" sx={{ color: 'grey.600' }} />
                  <Typography variant="h6" color="text.secondary">
                    {t('ticketNote')}
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  {state.item?.metadata?.ticket?.note}
                </Typography>
              </Stack>
            )}
            <StoreButtonResaleBox
              item={state.item}
              skuId={skuId}
              userActiveResaleItemRaffle={state.userActiveResaleItemRaffle}
              skuButtonActive={skuButtonActive}
              willResaleAvailable={willResaleAvailable}
              // 対象者限定かつユーザーが対象者でない場合
              restrected={restrected}
            />
            <SkuUnitSelectBottomSheet
              open={
                !!skuUnitSelectBottomSheetType &&
                skuUnitSelectBottomSheetType !== 'close'
              }
              item={state.item}
              itemId={state.itemId}
              skuValue={skuId}
              disabled={!skuId}
              label={
                skuUnitSelectBottomSheetType === 'buyNow'
                  ? t('purchaseNow', '今すぐ購入')
                  : t('addToCart', 'カートに追加')
              }
              formattedItemPrice={formattedItemPrice}
              skuSettings={state.item?.skuSettings}
              purchaseNumber={purchaseNumber}
              maximumPurchasableNumber={maximumPurchasableNumber}
              onChangePurchaseNumber={handleChangePurchaseNumber}
              onClose={() => setSkuUnitSelectBottomSheetType('close')}
              onSelect={sku => {
                setSkuId(sku?.id)
                setSkuTag(sku)
                onGetArtistStoreItemDetailWithSku(sku?.id)
              }}
              loading={state.addingToCart}
              onPurchase={
                skuUnitSelectBottomSheetType === 'cart'
                  ? handleAddToCart
                  : handleBuyNow
              }
              responsive={displayResponsive}
            />
            <BuyNowModal
              item={state.item}
              user={state.user}
              open={openBuyNow}
              sku={skuTag}
              onClose={() => setOpenBuyNow(() => false)}
              prepaidPointBalance={{ balance: state.point }}
              paymentSource={state.paymentSource}
              quantity={purchaseNumber}
              onPurchaseItem={async () => {
                onAnalyzeCheckout()
                await onPurchaseItem(purchaseNumber, totalPrice, skuId)
              }}
              statements={statements}
              totalPrice={totalPrice}
              formattedItemPrice={formattedItemPrice}
              verifyingSMSCode={state.verifyingSMSCode}
              sendingVerificationLoading={state.sendingVerificationLoading}
              openSMSVerificationModal={state.openSMSVerificationModal}
              onCloseSMSVerificationModal={onCloseSMSVerificationModal}
              onUserInfoSetting={onUserInfoSetting}
              onSendVerificationSMS={onSendVerificationSMS}
              onCreatePayment={onCreatePayment}
              onDeliveryInfoSetting={onDeliveryInfoSetting}
              onResendVerificationSMS={onResendVerificationSMS}
              onVerificationPhoneNumber={onVerificationPhoneNumber}
              phoneNumber={state.user?.formattedPhoneNumber ?? ''}
              onOpenPointPurchaseDrawer={() => setOpenPointPurchaseDrawer(true)}
            />
            {/* 無料アイテムの場合は、モーダルで確認 */}
            <ConfirmModal
              hasItem={state.hasItem}
              open={openConfirmFreeItem}
              itemPurchasing={state.itemPurchasing}
              onClick={async () => {
                await onPurchaseItem(purchaseNumber, itemPrice ?? 0, skuId)
                  .then(() => {
                    router.push({
                      pathname: generatePath(
                        routerPages.StoreItemCompleteFree,
                        {
                          artistId: state.artistId ?? '#',
                          itemId: state.itemId ?? '#',
                        }
                      ),
                      search: `${skuId ? `?skuId=${encodeSkuId(skuId)}` : ''}`,
                    })
                  })
                  .catch(e => showError(e))
                  .finally(() => {
                    setOpenConfirmFreeItem(false)
                  })
              }}
              onClose={() => {
                analytics?.track('click_cancel', {
                  applicationKey: config.applicationKey,
                  itemId: state.itemId,
                  artistId: state.artistId,
                  type: 'modal',
                  name: 'itemDetailConfirm',
                })
                setOpenConfirmFreeItem(false)
              }}
              name={state.item?.title}
              point={itemPrice ?? 0}
              formattedPoint={state.item?.formattedPrice ?? 0}
              purchaseNumber={purchaseNumber}
            />
          </Stack>
        )}
        {config.modules.store && (
          <InstantSearch
            ref={instantSearchRef}
            indexName={algolia.indexes.items}
            searchClient={client}
            refresh
            searchState={searchState}
            onSearchStateChange={newSearchState =>
              setSearchState(newSearchState)
            }
          >
            <Configure
              hitsPerPage={10}
              filters={algolia.filters.itemStoreArtist(
                state.artistId,
                publishedAtTimestampRef.current,
                state.itemId
              )}
            />
            <StoreItemsInfiniteHits
              artistId={state.artistId}
              userLoggedInId={state.userLoggedInId}
              onSelectItem={handleOnSelectItem}
              responsive={displayResponsive}
            />
            <StoreStateResults onCompleted={handleOnCompleted} />
          </InstantSearch>
        )}
        <NoEnoughPointModal
          itemId={state.itemId}
          artistId={state.artistId}
          prepaidPointId={state.prepaidPointId}
          open={openNoEnoughPoint}
          openPointPurchaseDrawer={() => {
            setOpenPointPurchaseDrawer(true)
            setOpenNoEnoughPoint(false)
          }}
          onClose={() => {
            analytics?.track('click_cancel', {
              applicationKey: config.applicationKey,
              prepaidPointId: state.prepaidPointId,
              itemId: state.itemId,
              artistId: state.artistId,
              name: 'itemDetailPoint',
              type: 'modal',
            })
            setOpenNoEnoughPoint(false)
          }}
        />
        {/* ポイントチャージ用ドロワー */}
        <PointChargeDrawer
          point={state.point ?? 0}
          onPurchase={(plan, paymentMethod) =>
            onPurchaseCharge(plan, paymentMethod, {
              // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
              successUrl: `${window.location.origin}${generatePath(
                routerPages.ArtistStoreItemDetail,
                {
                  artistId: router.query.artistId,
                  itemId: router.query.itemId,
                }
              )}?points=${plan.points}`,
              // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
              cancelUrl: `${window.location.origin}${generatePath(
                routerPages.ArtistStoreItemDetail,
                {
                  artistId: router.query.artistId,
                  itemId: router.query.itemId,
                }
              )}`,
              // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
              errorUrl: `${window.location.origin}${generatePath(
                routerPages.ArtistStoreItemDetail,
                {
                  artistId: router.query.artistId,
                  itemId: router.query.itemId,
                }
              )}`,
            })
          }
          open={openPointPurchaseDrawer}
          purchasablePrepaidPoints={state.purchasablePrepaidPoints}
          onClose={() => setOpenPointPurchaseDrawer(false)}
          chargeLoading={state.pointChargeLoading}
          paymentSource={state.paymentSource}
          loading={state.loading}
          enablePointCarrierPayment={state.enablePointCarrierPayment}
          pageName="artistStoreDetail"
          responsive={displayResponsive}
        />
        <CryptoWalletConnectDrawer
          open={openNftConnectDrawer}
          onClose={() => setOpenNftConnectDrawer(false)}
          connecting={false}
          onConnectWallet={walletType => {
            switch (walletType) {
              case 'web3auth':
                return setOpenWalletCreateConfirmModal(true)
              case 'metamask':
                return onConnectMetamask()
            }
          }}
          {...(displayResponsive
            ? {
                sx: {
                  '& .MuiPaper-root': {
                    maxWidth: '100%',
                  },
                },
              }
            : {})}
        />
        <CreateCryptoWalletConfirmModal
          open={openWalletCreateConfirmModal}
          loading={connecting}
          onClose={() => setOpenWalletCreateConfirmModal(false)}
          onCreate={onConnectWeb3auth}
        />
        <PersonalInfoModal
          open={openPersonalInfoModal}
          onClose={() => setOpenPersonalInfoModal(false)}
          user={state.user}
        />
        <NftInfoModal
          open={openNftInfoModal}
          onClose={() => setOpenNftInfoModal(false)}
        />

        <Require3DSecureModal
          open={state.openRequire3DSecureModal}
          onClose={onCloseRequire3DSecureModal}
        />

        <ExpiredCardModal
          open={state.openExpiredCardModal}
          onClose={onCloseExpiredCardModal}
        />

        <PaymentErrorModal
          open={state.openPaymentErrorModal}
          onClose={onClosePaymentErrorModal}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const StoreItemsInfiniteHits = connectInfiniteHits<
  InfiniteHitsProvided<Omit<StoreItemFragment, 'id'> & { objectID: string }> & {
    artistId: StoreItemsCellProps['artistId']
    userLoggedInId: StoreItemsCellProps['userLoggedInId']
    onSelectItem: (item: StoreItemFragment) => void
    responsive: StoreItemsCellProps['responsive']
  },
  {}
>(
  ({
    hits,
    hasMore,
    artistId,
    userLoggedInId,
    refineNext,
    onSelectItem,
    responsive,
  }) => {
    const items = hits.map(({ objectID, ...item }) => ({
      id: objectID,
      ...item,
    }))

    return (
      <StoreItemsCell
        items={items}
        artistId={artistId}
        userLoggedInId={userLoggedInId}
        hasNextPage={hasMore}
        onFetchMore={async () => refineNext()}
        onSelectItem={onSelectItem}
        responsive={responsive}
      />
    )
  }
)

type StoreItemsCellProps = {
  items: StoreItemFragment[]
  hasNextPage: boolean
  userLoggedInId: string
  onFetchMore: () => Promise<void>
  artistId: State['artistId']
  onSelectItem: (item: StoreItemFragment) => void
  responsive: boolean
}
const StoreItemsCell: Cell<StoreItemsCellProps> = ({
  hasNextPage,
  items,
  userLoggedInId,
  onFetchMore,
  artistId,
  onSelectItem,
  responsive,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { generatePath, routerPages } = useRouter()

  return (
    <CellFrame sx={{ py: 3, px: 5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" sx={{ py: 2 }}>
          {t('otherItem')}
        </Typography>
        <Button
          variant="text"
          sx={{ mr: -2 }}
          href={generatePath(routerPages.ArtistStore, { artistId })}
        >
          {t('toList')}
        </Button>
      </Stack>
      <ScrollFetcher
        loading={false}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid}
        itemComponentProps={
          responsive
            ? { item: true, xs: 4, sm: 3 }
            : { item: true, xs: 4, md: 3 }
        }
        nodes={items?.map((item, index) => (
          <StoreItemArea
            key={index}
            item={item}
            artistId={artistId}
            onClick={() => onSelectItem(item)}
            showMenberOnlyIcon={true}
            {...(responsive ? { responsive: true, aspectRatio: '1 / 1' } : {})}
          />
        ))}
      />
    </CellFrame>
  )
}

const ConfirmModal: Cell<{
  open: boolean
  hasItem: boolean
  itemPurchasing: boolean
  onClose: () => void
  onClick: () => void
  name?: string
  point: number | null
  formattedPoint: string | number | null
  purchaseNumber: number
}> = ({
  open,
  hasItem,
  itemPurchasing,
  onClose,
  onClick,
  name,
  point,
  purchaseNumber,
}) => {
  const { t } = useTranslation('storeItemDetail')

  return (
    <InformationModal
      open={open}
      title={
        point ? t('confirmPurchaseModalTitle') : t('confirmAcquireModalTitle')
      }
      content={
        point
          ? hasItem
            ? t('confirmAcquiredItemContent', { name })
            : t('confirmAcquireModalContent', {
                name,
                purchaseNumber,
              })
          : t('confirmAcquireModalContent', { name, purchaseNumber })
      }
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          size="large"
          loading={itemPurchasing}
          onClick={onClick}
        >
          {t('acquire')}
        </Button>
      }
      cancelText={t('close')}
      onClose={onClose}
    />
  )
}

const NoEnoughPointModal: Cell<{
  open: boolean
  onClose: () => void
  itemId: string
  artistId: string
  prepaidPointId: string
  openPointPurchaseDrawer: () => void
}> = ({
  open,
  onClose,
  itemId,
  artistId,
  prepaidPointId,
  openPointPurchaseDrawer,
}) => {
  const { analytics } = useAnalytics()
  const { t } = useTranslation('storeItemDetail')

  return (
    <InformationModal
      open={open}
      title={t('noEnoughPoint')}
      content={t('noEnoughPointContent')}
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          size="large"
          onClick={() => {
            analytics?.track('click_getPrepaidPoint', {
              applicationKey: config.applicationKey,
              name: 'itemDetail',
              itemId,
              artistId,
              prepaidPointId,
              type: 'modal',
            })
            openPointPurchaseDrawer()
          }}
        >
          {t('buyPoint')}
        </Button>
      }
      cancelText={t('close')}
      onClose={onClose}
    />
  )
}

type NftConnectModalProps = {
  open: boolean
  onClose: () => void
  onConnectMetamask: () => Promise<void>
}
export const NftConnectModal: Cell<NftConnectModalProps> = ({
  open,
  onClose,
  onConnectMetamask,
}) => {
  const { t } = useTranslation('nftItemDetail')
  const { showError } = useMessage()
  const [connecting, setConnecting] = useState(false)

  const wallets = [
    {
      src: 'images/nft/metaMask.png',
      title: 'MetaMask',
      onClick: onConnectMetamask,
    },
    /**
     * @description
     * 現段階ではmataMaskのみの対応
     * {
     *  src: 'images/nft/coinbaseWallet.png',
     *  title: 'Coinbase Wallet',
     *  onClick: async () => {},
     * },
     * {
     *  src: 'images/nft/fortmatic.png',
     *  title: 'Fortmatic',
     *  onClick: async () => {},
     * },
     */
  ]

  const onClickConnectWallet = async (onClick: () => Promise<void>) => {
    setConnecting(true)
    await onClick()
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        onClose()
      })

    return
  }

  return (
    <Drawer open={open} onClose={onClose}>
      <Stack spacing={2} sx={{ py: 4 }}>
        <Typography variant="body2" align="center">
          {t('nftConnectTitle')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'error.main' }}
          align="center"
        >
          {t('nftConnectNote')}
        </Typography>
      </Stack>
      <Divider />
      <List>
        {wallets.map(wallet => (
          <ListItem
            sx={{ minHeight: 68 }}
            divider
            key={wallet.title}
            secondaryAction={
              <Button
                size="medium"
                onClick={() => onClickConnectWallet(wallet.onClick)}
                disabled={connecting}
              >
                {t('workTogether')}
              </Button>
            }
          >
            <ListItemAvatar sx={{ display: 'flex' }}>
              <StorageImage
                target="common"
                src={wallet.src}
                width={35}
                height={35}
              />
            </ListItemAvatar>
            <ListItemText
              primary={wallet.title}
              slotProps={{ primary: { variant: 'subtitle1' } }}
            />
          </ListItem>
        ))}
      </List>
      <ListItemButton
        component={Stack}
        alignItems="center"
        onClick={onClose}
        disabled={connecting}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{ py: 2, color: 'primary.main' }}
        >
          {t('cancel')}
        </Typography>
      </ListItemButton>
    </Drawer>
  )
}

/**
 * チケットアイテム購入時に、個人情報を登録していない・電話番号認証を行なっていない場合表示するモーダル
 * https://zpl.io/z8067Ez
 */
const PersonalInfoModal = ({
  open,
  onClose,
  user,
}: {
  open: boolean
  onClose: () => void
  user: State['user']
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { routerPages } = useRouter()

  // ユーザーが「個人情報は登録している状態」かどうか
  const isRegistered = !!user?.address?.fullAddress

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={
        isRegistered
          ? t('PhoneNumberVerifyModalTitle', '電話番号の認証が必要です。')
          : t('PersonalInfoModalTitle', 'お客様情報の登録が必要です。')
      }
      content={
        isRegistered
          ? t(
              'PhoneNumberVerifyModalContent',
              'チケットアイテムを購入するには、登録されている電話番号の認証が必要です。'
            )
          : t(
              'PersonalInfoModalContent',
              'チケットアイテムを購入するには氏名、住所、電話番号の登録が必要です。'
            )
      }
      cancelText={t('PersonalInfoModalClose', '閉じる')}
      buttons={
        <Button
          size="large"
          sx={{ width: 212 }}
          href={
            isRegistered
              ? routerPages.DeliveryManagement
              : routerPages.DeliveryInfoSetting
          }
        >
          {isRegistered
            ? t('PhoneNumberVerifyModalButton', '電番認証する')
            : t('PersonalInfoModalButton', '登録画面へ')}
        </Button>
      }
    />
  )
}

const NftInfoModal: Cell<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useTranslation('storeItemDetail')

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      title={t('nftInfoModalTitle')}
      content={
        <Stack direction="column" justifyContent="center">
          <Typography variant="caption" textAlign="justify" sx={{ pb: 4 }}>
            {t('nftInfoModalDescription')}
          </Typography>

          <Stack direction="row" justifyContent="center">
            <Icons.Nft filledColor sx={{ mr: 2 }} />
            <Typography variant="caption" textAlign="justify" sx={{ pb: 4 }}>
              {t('nftInfoModalDescriptionForActive')}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="center">
            <Icons.Nft
              sx={{ p: 1, border: '1px solid', borderRadius: 10, mr: 2 }}
            />
            <Typography variant="caption" textAlign="justify">
              {t('nftInfoModalDescriptionForInactive')}
            </Typography>
          </Stack>
        </Stack>
      }
    />
  )
}
